import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Badge, Button, Card, PageHeader } from '@/components/ui/primitives';
import { EmptyState, ErrorState, LoadingState } from '@/components/DataState';
import { SortableTable, type SortableColumn } from '@/components/ui/SortableTable';
import { fetchSeoProjectOptions } from '@/data/sources';
import { fetchSeoSerpAnalysis } from '@/data/sources-seob';
import type { SerpAnalysisResult, SerpResultRow } from '@/types/seob';
import { ApiError } from '@/lib/api';
import { useAuth } from '@/auth/AuthContext';
import { useActiveDomain } from '@/hooks/useActiveDomain';
import { resolveActiveProjectId } from '@/pages/seo/domain-scope';
import { DomainChoiceGate } from '@/pages/seo/DomainChoiceGate';

/* /seo/serp-analysis — SERP Analysis (W2a). Read-only, source-attributed live
 * SERP for a keyword + location, from DataForSEO SERP organic-live.
 *
 * HONESTY + SAFETY INVARIANTS (W2a contract):
 *  - PAID (DataForSEO SERP live): NEVER auto-fires. Project options is a free
 *    list read; the SERP runs ONLY on an explicit click. No useQuery is wired to
 *    the paid read, so mounting this page charges nothing.
 *  - Source-attributed (source + provider fetched_at + cache indicator); every
 *    null field renders as an em-dash — nothing is invented.
 *  - Results render in the shared SortableTable (client-side sort only — free).
 *  - Honest states: idle / loading / error / not-configured (503).
 */

/** Location keys accepted by the contract's `location=<key>` param. */
const LOCATIONS: { key: string; label: string }[] = [
  { key: 'uk', label: 'United Kingdom' },
  { key: 'us', label: 'United States' },
  { key: 'ca', label: 'Canada' },
  { key: 'au', label: 'Australia' },
  { key: 'ie', label: 'Ireland' },
];

function sourceLabel(source: string): string {
  if (source.startsWith('dataforseo.serp')) return 'DataForSEO SERP';
  if (source.startsWith('dataforseo')) return 'DataForSEO';
  return source;
}

function formatInt(n: number): string {
  return n.toLocaleString('en-GB');
}

interface Panel {
  title: string;
  body: string;
}

type SectionState<T> =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'error'; panel: Panel }
  | { status: 'done'; data: T };

function describeError(err: unknown): Panel {
  if (err instanceof ApiError) {
    if (err.status === 503 || err.code === 'provider_unavailable') {
      return {
        title: 'SERP analysis isn’t connected yet',
        body: 'Live SERP data needs the DataForSEO connection, which isn’t configured for this environment yet. Nothing was fetched and nothing was charged — an honest “not configured” state, never placeholder data.',
      };
    }
    if (err.status === 402 || err.code === 'budget_exceeded') {
      return {
        title: 'Monthly provider budget reached',
        body: 'This lookup would exceed your workspace’s monthly provider budget, so it was refused before any provider call was made — nothing was spent. It resumes when the budget resets, or after you raise the cap on Spend & Budget.',
      };
    }
    if (err.status === 429 || err.code === 'rate_limited') {
      return {
        title: 'Provider rate limit reached',
        body: 'This workspace has hit the provider rate limit, so nothing was fetched or charged. Try again as the window rolls over.',
      };
    }
    if (err.status === 502 || err.code === 'provider_error') {
      return {
        title: 'The provider request failed',
        body: `${err.message ? `${err.message} ` : ''}Nothing was stored or charged — try again in a moment.`,
      };
    }
    if (err.status === 404) {
      return {
        title: 'Project not found',
        body: 'This SEO project isn’t available to your workspace. Pick another project and try again.',
      };
    }
    return {
      title: 'The lookup could not be completed',
      body: err.message || 'The data service returned an unexpected response. Nothing was charged — try again.',
    };
  }
  return {
    title: 'The lookup could not be completed',
    body: err instanceof Error ? err.message : 'Unexpected error — nothing was charged. Try again.',
  };
}

function SourceFooter({
  source,
  fetchedAt,
  cacheHit,
}: {
  source: string;
  fetchedAt: string | null;
  cacheHit: boolean;
}) {
  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 border-t border-border-subtle px-3 py-2 text-[11px] text-text-tertiary">
      <Badge tone="neutral">Source: {sourceLabel(source)}</Badge>
      <Badge tone={cacheHit ? 'neutral' : 'success'}>
        {cacheHit ? 'Served from cache' : 'Live result'}
      </Badge>
      <span>{fetchedAt ? `Last updated ${fetchedAt}` : 'Update time not reported'}</span>
    </div>
  );
}

const SERP_COLUMNS: SortableColumn<SerpResultRow>[] = [
  {
    key: 'position',
    header: 'Pos',
    align: 'right',
    numeric: true,
    sortable: true,
    sortValue: (r) => r.position,
    render: (r) => (r.position === null ? '—' : formatInt(r.position)),
    cellClassName: 'font-mono text-text-primary',
  },
  {
    key: 'domain',
    header: 'Domain',
    sortable: true,
    sortValue: (r) => r.domain,
    render: (r) => r.domain,
    truncate: true,
    maxWidthClass: 'max-w-[200px]',
    title: (r) => r.domain,
    cellClassName: 'font-medium text-text-primary',
  },
  {
    key: 'title',
    header: 'Title',
    render: (r) => r.title ?? '—',
    truncate: true,
    maxWidthClass: 'max-w-[320px]',
    title: (r) => r.title ?? undefined,
    cellClassName: 'text-text-secondary',
  },
  {
    key: 'url',
    header: 'URL',
    render: (r) => r.url ?? '—',
    truncate: true,
    maxWidthClass: 'max-w-[240px]',
    title: (r) => r.url ?? undefined,
    cellClassName: 'text-text-tertiary',
  },
  {
    key: 'type',
    header: 'Type',
    sortable: true,
    sortValue: (r) => r.type,
    render: (r) => r.type ?? '—',
    cellClassName: 'text-text-tertiary',
  },
];

export function SeoSerpAnalysisPage() {
  const { activeWorkspace } = useAuth();
  const canManage = activeWorkspace?.role === 'owner' || activeWorkspace?.role === 'operator';
  const navigate = useNavigate();

  const projectsQ = useQuery({
    queryKey: ['seo', 'project-options'],
    queryFn: fetchSeoProjectOptions,
  });
  const projects = projectsQ.data ?? [];
  const { active, select } = useActiveDomain();

  const projectId = resolveActiveProjectId(active, projects);

  const [keyword, setKeyword] = useState('');
  const [location, setLocation] = useState(LOCATIONS[0].key);
  const [serp, setSerp] = useState<SectionState<SerpAnalysisResult>>({ status: 'idle' });

  const runSerp = async () => {
    if (projectId === null || serp.status === 'loading') return;
    const trimmed = keyword.trim();
    if (trimmed === '') return;
    setSerp({ status: 'loading' });
    try {
      const data = await fetchSeoSerpAnalysis(projectId, trimmed, location);
      setSerp({ status: 'done', data });
    } catch (err) {
      setSerp({ status: 'error', panel: describeError(err) });
    }
  };

  const onProjectChange = (id: string) => {
    void select(Number(id));
    setSerp({ status: 'idle' });
  };

  if (projectsQ.isPending) {
    return (
      <div className="space-y-6">
        <PageHeader title="SERP Analysis" />
        <LoadingState rows={4} />
      </div>
    );
  }
  if (projectsQ.error) {
    return (
      <div className="space-y-6">
        <PageHeader title="SERP Analysis" />
        <ErrorState error={projectsQ.error} onRetry={() => void projectsQ.refetch()} />
      </div>
    );
  }
  if (projects.length > 0 && projectId === null) {
    return <DomainChoiceGate options={projects} onSelect={(id) => void select(Number(id))} />;
  }
  if (projects.length === 0) {
    return (
      <div className="space-y-6">
        <PageHeader title="SERP Analysis" />
        <EmptyState
          title="No project to explore yet"
          teach="SERP Analysis runs against an SEO project. Add your first domain on the Projects page, then come back here to see who ranks for a keyword."
          action={
            <Button variant="primary" onClick={() => navigate('/seo/projects')}>
              Go to Projects
            </Button>
          }
        />
      </div>
    );
  }

  const inputClass =
    'h-control rounded-md border border-border-default bg-canvas px-3 text-sm text-text-primary outline-none focus:border-ai focus:ring-2 focus:ring-ai/30';

  const projectSelector = (
    <label className="flex items-center gap-2">
      <span className="text-xs font-medium text-text-secondary">Domain</span>
      <select
        value={projectId ?? ''}
        onChange={(e) => onProjectChange(e.target.value)}
        className={inputClass}
        aria-label="SEO project"
      >
        {projects.map((p) => (
          <option key={p.id} value={p.id}>
            {p.label}
          </option>
        ))}
      </select>
    </label>
  );

  return (
    <div className="space-y-6">
      <PageHeader
        title="SERP Analysis"
        subtitle="Who ranks for a keyword right now, from licensed DataForSEO SERP data — loaded on demand, source-attributed, and honest about every missing field."
        actions={projectSelector}
      />

      {!canManage && (
        <div role="status" className="rounded-md border border-border-subtle bg-inset/50 px-3 py-2 text-sm text-text-secondary">
          SERP analysis uses paid provider data, so it’s limited to workspace owners and operators.
        </div>
      )}

      <Card className="p-4">
        <div className="flex flex-wrap items-center gap-2">
          <Badge tone="engine">DataForSEO SERP</Badge>
          <span className="text-sm font-medium text-text-primary">Live organic SERP</span>
          <div className="ml-auto flex items-center gap-2">
            <span className="text-xs text-text-tertiary">Paid · budget-guarded · 7-day cache</span>
          </div>
        </div>
        <p className="mt-1 text-sm text-text-secondary">
          Enter a keyword and location to see the live organic results and SERP features. Runs only
          when you press the button — nothing is fetched or charged on load.
        </p>

        <form
          className="mt-3 flex flex-wrap items-center gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            void runSerp();
          }}
        >
          <input
            type="text"
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            placeholder="e.g. community growth platform"
            aria-label="Keyword"
            className={`${inputClass} min-w-[14rem] flex-1`}
            disabled={!canManage}
          />
          <label className="flex items-center gap-2">
            <span className="sr-only">Location</span>
            <select
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              className={inputClass}
              aria-label="SERP location"
              disabled={!canManage}
            >
              {LOCATIONS.map((l) => (
                <option key={l.key} value={l.key}>
                  {l.label}
                </option>
              ))}
            </select>
          </label>
          <Button
            type="submit"
            variant="primary"
            disabled={!canManage || serp.status === 'loading' || keyword.trim() === ''}
            title={canManage ? undefined : 'Owner/operator only — this uses paid provider data'}
          >
            {serp.status === 'loading'
              ? 'Loading…'
              : serp.status === 'done'
                ? 'Refresh'
                : 'Analyse SERP'}
          </Button>
        </form>

        <div className="mt-4">
          {serp.status === 'idle' && (
            <p className="text-sm text-text-tertiary">
              No data yet — enter a keyword and analyse the live SERP.
            </p>
          )}
          {serp.status === 'loading' && <LoadingState rows={4} />}
          {serp.status === 'error' && (
            <div role="status" className="rounded-md border border-border-default bg-surface px-4 py-3">
              <div className="text-sm font-medium text-text-primary">{serp.panel.title}</div>
              <p className="mt-1 text-sm text-text-secondary">{serp.panel.body}</p>
            </div>
          )}
          {serp.status === 'done' &&
            (serp.data.serp.length === 0 ? (
              <div className="rounded-md border border-border-subtle bg-canvas p-3">
                <p className="text-sm text-text-secondary">
                  The provider returned no organic results for this query — an honest empty result,
                  not an error.
                </p>
                <div className="mt-2">
                  <SourceFooter
                    source={serp.data.source}
                    fetchedAt={serp.data.fetched_at}
                    cacheHit={serp.data.cache_hit}
                  />
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                {serp.data.features.length > 0 && (
                  <div className="flex flex-wrap items-center gap-1.5">
                    <span className="text-[11px] uppercase tracking-wide text-text-tertiary">
                      SERP features
                    </span>
                    {serp.data.features.map((f) => (
                      <Badge key={f} tone="neutral">
                        {f}
                      </Badge>
                    ))}
                  </div>
                )}
                <div className="overflow-hidden rounded-lg border border-border-subtle bg-surface">
                  <SortableTable
                    rows={serp.data.serp}
                    columns={SERP_COLUMNS}
                    getRowKey={(r, i) => `${r.domain}-${i}`}
                    minWidthClass="min-w-[760px]"
                    caption="Live organic SERP results by position"
                  />
                  <SourceFooter
                    source={serp.data.source}
                    fetchedAt={serp.data.fetched_at}
                    cacheHit={serp.data.cache_hit}
                  />
                </div>
              </div>
            ))}
        </div>
      </Card>
    </div>
  );
}
